import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  Activity, DollarSign, Cpu, Image, GitBranch, Key, Users, Eye,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { clientActions } from '../../../actions/client';
import { apiErrorText } from '../../../utils/apiError';
import AppScreenWrapper from '../../../components/app-shell/AppScreenWrapper';
import Sidebar from '../../../components/app-shell/Sidebar';
import Topbar from '../../../components/app-shell/Topbar';
import ScreenOrbs from '../../../components/app-shell/ScreenOrbs';
import useClientData from '../../../hooks/useClientData';

// ─── Constants ───────────────────────────────────────────────────────────────

const KEY_COLORS = [
  '#6366F1', '#EC4899', '#10B981', '#F59E0B',
  '#3B82F6', '#EF4444', '#8B5CF6', '#14B8A6', '#F97316', '#84CC16',
];

const PERIOD_OPTIONS = [
  { label: '24H', value: '24h' },
  { label: '7D', value: '7d' },
  { label: '30D', value: '30d' },
  { label: '90D', value: '90d' },
  { label: '12M', value: '12m' },
];

// Usage data before this date is not shown to clients (enforced server-side too).
const USAGE_FLOOR = '2026-06-01';
const todayStr = () => new Date().toISOString().slice(0, 10);

// Preset toggle button style (shared by presets + the Custom toggle)
const periodBtnStyle = (active) => ({
  height: 32, padding: '0 14px', borderRadius: 7, cursor: 'pointer', fontSize: 13,
  background: active ? 'rgba(255,255,255,.08)' : 'transparent',
  border: `1px solid ${active ? 'rgba(255,77,141,.35)' : 'transparent'}`,
  color: active ? '#FFC2B5' : 'var(--text-3)',
  fontWeight: active ? 600 : 400,
  transition: 'all 0.15s ease',
});

// Recharts renders SVG attrs — CSS var() doesn't resolve there, literals only
const CHART_GRID = 'rgba(255,255,255,.07)';
const CHART_AXIS = 'rgba(255,255,255,.45)';

// ─── Formatters ───────────────────────────────────────────────────────────────

const fmt = (n) => Intl.NumberFormat('en', { notation: 'compact' }).format(n);

// Hour buckets arrive as UTC strings without a zone ('...T14:00:00') — parsing
// them as local time shifts every hour label by the user's offset
const parseUtcHour = (v) => new Date(String(v).endsWith('Z') ? v : `${v}Z`);

const fmtBucket = (v, period) => {
  if (period === '24h') {
    const d = parseUtcHour(v);
    return isNaN(d.getTime()) ? v : d.toLocaleTimeString('en', { hour: 'numeric', hour12: true });
  }
  if (period === '12m') {
    const d = new Date(v + 'T00:00:00');
    return isNaN(d.getTime()) ? v : d.toLocaleDateString('en', { month: 'short' });
  }
  const d = new Date(v + 'T00:00:00');
  return isNaN(d.getTime()) ? v : d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
};

const fmtBucketFull = (v, period) => {
  if (period === '24h') {
    const d = parseUtcHour(v);
    return isNaN(d.getTime()) ? v : d.toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', hour12: true });
  }
  const d = new Date(v + 'T00:00:00');
  if (isNaN(d.getTime())) return v;
  if (period === '12m') return d.toLocaleDateString('en', { month: 'long', year: 'numeric' });
  return d.toLocaleDateString('en', { month: 'long', day: 'numeric', year: 'numeric' });
};

const fmtHour = (v) => {
  const d = parseUtcHour(v);
  return isNaN(d.getTime()) ? v : d.toLocaleTimeString('en', { hour: 'numeric', hour12: true });
};

const fmtHourFull = (v) => {
  const d = parseUtcHour(v);
  return isNaN(d.getTime()) ? v : d.toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', hour12: true });
};

// ─── Build stacked chart data from flat series_by_key array ──────────────────

const buildStackedData = (rows, valueField, bucketField = 'bucket') => {
  const map = {};
  for (const row of rows) {
    const b = row[bucketField];
    if (!map[b]) map[b] = { bucket: b };
    map[b][row.client_api_key_id] = (map[b][row.client_api_key_id] || 0) + (row[valueField] || 0);
  }
  return Object.values(map).sort((a, b) => (a.bucket > b.bucket ? 1 : -1));
};

// ─── Tooltip style ────────────────────────────────────────────────────────────

const tt = {
  contentStyle: {
    background: 'rgba(12, 12, 20, 0.97)',
    border: '1px solid rgba(255,255,255,.12)',
    borderRadius: 10,
    fontSize: 12,
    color: '#fff',
    padding: '10px 14px',
    boxShadow: '0 12px 40px rgba(0,0,0,0.7)',
    backdropFilter: 'blur(16px)',
    minWidth: 160,
  },
  itemStyle: { color: '#fff', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 6 },
  labelStyle: { color: 'rgba(255,255,255,.55)', marginBottom: 6, fontWeight: 600, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase' },
  wrapperStyle: { zIndex: 9999 },
};

// ─── Small building blocks ────────────────────────────────────────────────────

function Skeleton({ w = '100%', h = 20, r = 8 }) {
  return (
    <div style={{ width: w, height: h, borderRadius: r, background: 'rgba(255,255,255,.06)', animation: 'shimmer 1.5s infinite' }} />
  );
}

const KpiCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="card" style={{ padding: '14px 16px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={14} color={color} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
    </div>
    <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4 }}>{sub}</div>}
  </div>
);

const SectionHeader = ({ title, sub }) => (
  <div style={{ padding: '18px 20px 0' }}>
    <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>{title}</h3>
    {sub && <p style={{ fontSize: 12, color: 'var(--text-3)', margin: '3px 0 0' }}>{sub}</p>}
  </div>
);

// ─── Key Leaderboard ──────────────────────────────────────────────────────────

const KeyLeaderboard = ({ perKey, keyColorMap, totalCost, selectedKey, onSelectKey, descriptionById, selfClientId, onToggleKeyActive, togglingKeyId, canManageKeys }) => {
  if (!perKey.length) return null;
  const HEADS = ['#', 'API key', 'LLM calls', 'LLM cost', 'Job calls', 'Job cost', 'Calls', 'Cost', 'Share'];
  return (
    <div className="usage-lb-scroll" style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {HEADS.map((h, i) => (
              <th key={i} style={{ padding: '8px 12px', textAlign: i <= 1 ? 'left' : 'right', fontSize: 11, fontWeight: 600, color: 'var(--text-4)', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {perKey.map((k, idx) => {
            const color = keyColorMap[k.client_api_key_id] || '#888';
            const sharePct = totalCost > 0 ? (k.total_cost / totalCost) * 100 : 0;
            const jobCalls = (k.image_jobs || 0) + (k.workflow_runs || 0);
            const jobCost = (k.image_cost || 0) + (k.workflow_cost || 0);
            const isTop = idx === 0 && k.total_cost > 0;
            // Name the owner on every key the viewer does not own. Falls back to
            // the old sub-account-only rule if the API predates `self_client_id`.
            const showOwner = Boolean(k.owner_name) && (
              selfClientId ? k.owner_client_id !== selfClientId : k.owner_account_type === 'sub'
            );
            return (
              <tr
                key={k.client_api_key_id}
                className="usage-lb-row"
                onClick={() => onSelectKey(k.client_api_key_id)}
                style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: selectedKey === k.client_api_key_id ? `${color}18` : 'transparent', opacity: selectedKey && selectedKey !== k.client_api_key_id ? 0.35 : 1, transition: 'opacity 0.15s ease, background 0.15s ease' }}
              >
                <td style={{ padding: '10px 12px', color: 'var(--text-4)', fontWeight: 600 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
                    {idx + 1}
                  </div>
                </td>
                <td style={{ padding: '10px 12px', minWidth: 160 }}>
                  <div style={{ fontWeight: 600 }} title={descriptionById[k.client_api_key_id] || undefined}>{k.api_key_name}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
                    {/* Shown for shared keys too — the prefix+preview is how you
                        tell two keys apart when their names collide. The guard is
                        for legacy rows with no prefix, not a permission check. */}
                    {k.api_key_prefix && (
                      <span className="mono" style={{ fontSize: 10, color: '#A5B4FC' }}>
                        {k.api_key_prefix}{k.key_preview && k.key_preview !== '...' ? k.key_preview : '...'}
                      </span>
                    )}
                    {showOwner && (
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#F59E0B', background: 'rgba(245,158,11,0.1)', padding: '1px 5px', borderRadius: 3 }}>
                        {k.owner_name}
                      </span>
                    )}
                    {k.is_shared && (
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#8B5CF6', background: 'rgba(139,92,246,0.12)', padding: '1px 5px', borderRadius: 3 }}>
                        Shared
                      </span>
                    )}
                    {k.is_active === false && (
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#F59E0B', background: 'rgba(245,158,11,0.12)', padding: '1px 5px', borderRadius: 3 }}>
                        Disabled
                      </span>
                    )}
                    {/* This screen is where a sub-account sees the keys shared
                        with it, so it is the only place a grantee can act on one.
                        stopPropagation because the row click filters the charts. */}
                    {onToggleKeyActive && canManageKeys && k.is_active !== undefined && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onToggleKeyActive(k); }}
                        disabled={togglingKeyId === k.client_api_key_id}
                        title={k.is_active ? 'Disable this key' : 'Enable this key'}
                        aria-label={k.is_active ? 'Disable this key' : 'Enable this key'}
                        style={{
                          fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                          cursor: 'pointer', background: 'transparent',
                          border: `1px solid ${k.is_active ? 'var(--border)' : 'rgba(16,185,129,0.4)'}`,
                          color: k.is_active ? 'var(--text-4)' : '#10B981',
                          opacity: togglingKeyId === k.client_api_key_id ? 0.4 : 1,
                        }}
                      >
                        {k.is_active ? 'Disable' : 'Enable'}
                      </button>
                    )}
                  </div>
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>{Number(k.llm_calls || 0).toLocaleString()}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>${(k.llm_cost || 0).toFixed(4)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>{Number(jobCalls).toLocaleString()}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>${jobCost.toFixed(4)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{Number(k.total_calls).toLocaleString()}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: isTop ? 'var(--red)' : '#fff' }}>${(k.total_cost || 0).toFixed(4)}</td>
                <td style={{ padding: '10px 24px 10px 12px', minWidth: 100 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ flex: 1, height: 4, borderRadius: 2, background: 'rgba(255,255,255,.08)' }}>
                      <div style={{ width: `${Math.min(sharePct, 100)}%`, height: '100%', borderRadius: 2, background: color }} />
                    </div>
                    <span style={{ fontSize: 10, color: 'var(--text-4)', minWidth: 28, textAlign: 'right' }}>{sharePct.toFixed(0)}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────

export default function ApiUsageScreen() {
  const navigate = useNavigate();
  const { balance: shellBalance, clientName: shellName, clientEmail: shellEmail } = useClientData();
  const [period, setPeriod] = useState('30d');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  const [rawSelectedKey, setSelectedKey] = useState(null);
  const [descriptionById, setDescriptionById] = useState({});
  // Main-account-only "view as a member" preview.
  const [subAccounts, setSubAccounts] = useState([]);
  const [viewAsId, setViewAsId] = useState(null);

  const customReady = period === 'custom' && customRange.from && customRange.to;
  const reqIdRef = useRef(0);

  const fetchUsage = useCallback(async () => {
    // Wait for both custom dates; clear stale preset charts while waiting.
    if (period === 'custom' && !(customRange.from && customRange.to)) {
      setData(null);
      setError(false);
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const useCustom = period === 'custom' ? customRange : null;
      const res = await clientActions.getApiUsage(period, null, useCustom, viewAsId);
      if (reqId !== reqIdRef.current) return; // a newer request superseded this one
      setData(res?.data ?? null);
    } catch {
      if (reqId !== reqIdRef.current) return;
      setData(null);
      setError(true);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [period, customRange, viewAsId]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  // Members list, which doubles as the "am I a main account?" signal. It must not
  // be derived from is_main_account: while previewing, the response is rendered
  // from the member's viewpoint and that flag is false — reading it would make
  // the control disappear the moment you used it.
  useEffect(() => {
    (async () => {
      try {
        const res = await clientActions.getSubAccounts();
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setSubAccounts(list.filter((s) => s && s.client_id));
      } catch {
        setSubAccounts([]); // sub/standard accounts get [] anyway — control stays hidden
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await clientActions.getApiKeys();
        const keys = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        const map = {};
        keys.forEach(k => { if (k.description) map[k.client_api_key_id] = k.description; });
        setDescriptionById(map);
      } catch (e) {
        console.warn('api-usage: key descriptions unavailable', e?.message);
      }
    })();
  }, []);

  // ─── Derived data (memoized to stabilize references) ──────────────────────

  const summary = useMemo(() => data?.summary || {}, [data]);
  const perKey = useMemo(() => data?.per_key || [], [data]);
  const seriesByKey = useMemo(() => data?.series_by_key || [], [data]);
  const peakByKey = useMemo(() => data?.peak_by_key || [], [data]);
  const tokensSeries = useMemo(() => data?.tokens_series || [], [data]);
  const isMainAccount = data?.is_main_account || false;
  const totalCost = summary.total_cost || 0;

  // Sharing context: a sub-account can be granted visibility into other accounts
  // or individual keys by the main account. Without this the extra rows would
  // just appear unexplained.
  const access = data?.access || null;
  const selfClientId = access?.self_client_id || null;
  const viewingAs = access?.viewing_as || null;
  const canPreview = subAccounts.length > 0;

  // Plain-language answer to "what do they actually have access to?", shown in
  // the preview banner so the admin doesn't have to infer it from the charts.
  const previewScopeText = useMemo(() => {
    if (!viewingAs || !access) return null;
    const keys = viewingAs.visible_key_count ?? 0;
    const keyPart = `${keys} API key${keys === 1 ? '' : 's'} visible`;
    if (access.scope === 'all') return `Entire organization · ${keyPart}`;
    if (access.scope === 'own') return `Own usage only · ${keyPart}`;
    const bits = [];
    if (access.shared_account_count > 0) bits.push(`${access.shared_account_count} shared account${access.shared_account_count === 1 ? '' : 's'}`);
    if (access.shared_key_count > 0) bits.push(`${access.shared_key_count} shared key${access.shared_key_count === 1 ? '' : 's'}`);
    return `${bits.length ? `Own usage + ${bits.join(' + ')}` : 'Own usage only (nothing shared resolves)'} · ${keyPart}`;
  }, [viewingAs, access]);
  const sharedNotice = useMemo(() => {
    if (!access?.has_shared_access) return null;
    if (access.scope === 'all') return 'Your administrator has shared usage for the entire organization with you.';
    const bits = [];
    if (access.shared_account_count > 0) {
      bits.push(`${access.shared_account_count} account${access.shared_account_count > 1 ? 's' : ''}`);
    }
    if (access.shared_key_count > 0) {
      bits.push(`${access.shared_key_count} API key${access.shared_key_count > 1 ? 's' : ''}`);
    }
    if (!bits.length) return null;
    return `Your administrator has shared ${bits.join(' and ')} with you, in addition to your own usage.`;
  }, [access]);

  const keyColorMap = useMemo(() => {
    const map = {};
    perKey.forEach((k, i) => { map[k.client_api_key_id] = KEY_COLORS[i % KEY_COLORS.length]; });
    return map;
  }, [perKey]);

  // Clamp the selection: a key that vanished after a period switch must not
  // leave everything dimmed with no visible active chip
  const selectedKey = useMemo(
    () => (rawSelectedKey && perKey.some(k => k.client_api_key_id === rawSelectedKey) ? rawSelectedKey : null),
    [rawSelectedKey, perKey]
  );

  const activeSeriesKeys = useMemo(() => {
    const ids = new Set(seriesByKey.map(r => r.client_api_key_id));
    return perKey.filter(k => ids.has(k.client_api_key_id));
  }, [seriesByKey, perKey]);

  const activePeakKeys = useMemo(() => {
    const ids = new Set(peakByKey.map(r => r.client_api_key_id));
    return perKey.filter(k => ids.has(k.client_api_key_id));
  }, [peakByKey, perKey]);

  const spendChartData = useMemo(() => buildStackedData(seriesByKey, 'total_cost'), [seriesByKey]);
  const callsChartData = useMemo(() => buildStackedData(seriesByKey, 'total_calls'), [seriesByKey]);
  const peakCallsData = useMemo(() => buildStackedData(peakByKey, 'total_calls', 'hour'), [peakByKey]);

  const toggleKey = useCallback((keyId) => {
    setSelectedKey(prev => prev === keyId ? null : keyId);
  }, []);

  // Disable / enable a key from the usage table. This is the grantee's only
  // surface: AccountScreen lists a client's OWN keys, so a sub-account acting on
  // a key its parent shared with it can only reach that key here.
  const [togglingKeyId, setTogglingKeyId] = useState(null);
  const [powerForm, setPowerForm] = useState(null); // null | { key, next }
  const [powerReason, setPowerReason] = useState('');
  const [powerError, setPowerError] = useState('');

  // Confirm BOTH directions. Re-enabling is the dangerous one here: the key may
  // have been disabled precisely because it leaked, and unlike AccountScreen
  // this surface has no "you disabled it yourself" signal to safely skip it.
  const handleToggleKeyActive = useCallback((k) => {
    setPowerReason('');
    setPowerError('');
    setPowerForm({ key: k, next: !k.is_active });
  }, []);

  const applyKeyPower = useCallback(async () => {
    if (!powerForm) return;
    const { key, next } = powerForm;
    setTogglingKeyId(key.client_api_key_id);
    setPowerError('');
    try {
      const res = await clientActions.setApiKeyActive(
        key.client_api_key_id, next, powerReason.trim() || undefined
      );
      await fetchUsage();
      // The DB is authoritative, but a warm auth-cache entry can keep the key
      // working for up to an hour. Staying silent would show success over a
      // still-live credential during an incident.
      if (!next && res && res.cache_invalidated === false) {
        setPowerError('Disabled in the database, but the auth cache could not be cleared — this key may keep working for up to 1 hour.');
        return; // keep the dialog open so the warning is actually read
      }
      setPowerForm(null);
    } catch (err) {
      setPowerError(apiErrorText(err, next ? 'Failed to enable key' : 'Failed to disable key'));
    } finally {
      setTogglingKeyId(null);
    }
  }, [powerForm, powerReason, fetchUsage]);
  const handleLegendClick = useCallback((payload) => {
    if (payload && payload.dataKey) toggleKey(payload.dataKey);
  }, [toggleKey]);
  // Label formatting follows the granularity the server actually returned
  // (custom ranges pick hour/day/month by span), not the raw preset.
  const granularity = data?.range?.granularity;
  const displayPeriod = granularity === 'hour' ? '24h' : granularity === 'month' ? '12m' : '30d';
  const isCustomRange = data?.range?.is_custom || false;
  const bucketTick = useCallback((v) => fmtBucket(v, displayPeriod), [displayPeriod]);
  const bucketTickFull = useCallback((v) => fmtBucketFull(v, displayPeriod), [displayPeriod]);

  // Honest range caption: reflects the clamped window the server returned, so
  // a "12M" preset that got clamped to the floor never lies about its span.
  const rangeCaption = useMemo(() => {
    const r = data?.range;
    if (!r) return null;
    const opt = { month: 'short', day: 'numeric', year: 'numeric' };
    return `Showing ${new Date(r.from).toLocaleDateString('en', opt)} – ${new Date(r.to).toLocaleDateString('en', opt)}`;
  }, [data]);

  const insights = useMemo(() => {
    const list = [];
    if (!perKey.length) return list;
    const topSpender = perKey[0];
    if (topSpender.total_cost > 0) {
      const pct = totalCost > 0 ? ((topSpender.total_cost / totalCost) * 100).toFixed(0) : 0;
      list.push({ type: 'cost', text: `"${topSpender.api_key_name}" is your top spender — $${Number(topSpender.total_cost).toFixed(4)} (${pct}% of total spend)` });
    }
    const topCaller = [...perKey].sort((a, b) => (b.total_calls || 0) - (a.total_calls || 0))[0];
    if (topCaller && topCaller.client_api_key_id !== topSpender.client_api_key_id) {
      list.push({ type: 'calls', text: `"${topCaller.api_key_name}" leads in call volume — ${Number(topCaller.total_calls).toLocaleString()} calls this period` });
    }
    const buckets = [...new Set(seriesByKey.map(r => r.bucket))].sort();
    if (buckets.length >= 2) {
      const lastB = buckets[buckets.length - 1];
      const prevB = buckets[buckets.length - 2];
      const lastTotal = seriesByKey.filter(r => r.bucket === lastB).reduce((s, r) => s + (r.total_calls || 0), 0);
      const prevTotal = seriesByKey.filter(r => r.bucket === prevB).reduce((s, r) => s + (r.total_calls || 0), 0);
      if (prevTotal > 0) {
        const pctChg = ((lastTotal - prevTotal) / prevTotal) * 100;
        if (pctChg >= 15) list.push({ type: 'up', text: `Latest period call volume is up ${pctChg.toFixed(0)}% vs the previous period` });
        else if (pctChg <= -15) list.push({ type: 'down', text: `Latest period call volume is down ${Math.abs(pctChg).toFixed(0)}% vs the previous period` });
      }
    }
    if (peakByKey.length > 0) {
      const hourMap = {};
      for (const r of peakByKey) { hourMap[r.hour] = (hourMap[r.hour] || 0) + (r.total_calls || 0); }
      const peak = Object.entries(hourMap).sort((a, b) => b[1] - a[1])[0];
      if (peak && Number(peak[1]) > 0) {
        const d = parseUtcHour(peak[0]);
        const h = isNaN(d.getTime()) ? peak[0] : d.toLocaleTimeString('en', { hour: 'numeric', hour12: true });
        list.push({ type: 'peak', text: `Peak activity ${isCustomRange ? 'in the last day of the range' : 'today'} was at ${h} with ${Number(peak[1]).toLocaleString()} calls` });
      }
    }
    const idle = perKey.filter(k => !k.total_calls && !k.total_cost);
    if (idle.length > 0) list.push({ type: 'idle', text: `${idle.length} key${idle.length > 1 ? 's' : ''} had zero activity this period` });
    return list;
  }, [perKey, seriesByKey, peakByKey, totalCost, isCustomRange]);

  const isEmpty = !error && !perKey.length && !seriesByKey.length;

  return (
    <AppScreenWrapper>
      <style>{`
        @keyframes shimmer { 0%,100% { opacity:.5 } 50% { opacity:1 } }
        .usage-lb-row:hover { background: rgba(255,255,255,.03); }
        .usage-date-input {
          height: 32px; padding: 0 10px; border-radius: 7px; font-size: 13px;
          background: rgba(255,255,255,.04); border: 1px solid var(--border);
          color: var(--text-2); color-scheme: dark; cursor: pointer;
        }
        .usage-date-input:focus { outline: none; border-color: rgba(255,77,141,.35); }
      `}</style>
      <div className="screen-root" style={{ display: 'flex' }}>
        <Sidebar balance={shellBalance} name={shellName} email={shellEmail} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
          <ScreenOrbs tone="indigo" />
          <Topbar title="API usage" name={shellName} />
          <div className="scroll-y app-screen-content" style={{ flex: 1, padding: '28px 32px 48px', position: 'relative', zIndex: 1 }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>

              {/* ─── Controls row ─── */}
              <div className="row usage-controls" style={{ gap: 12, marginBottom: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate('/account')}>← Account</button>

                  {/* Main-account only: preview the page as a member sees it.
                      Rendered from the members list, not is_main_account, which
                      flips to false while previewing. */}
                  {canPreview && (
                    <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                      <button
                        onClick={() => {
                          setSelectedKey(null);
                          setViewAsId(viewAsId ? null : subAccounts[0].client_id);
                        }}
                        aria-pressed={Boolean(viewAsId)}
                        title={viewAsId ? 'Return to your own view' : 'See this page as one of your members sees it'}
                        style={{
                          height: 32, padding: '0 12px', borderRadius: 7, cursor: 'pointer', fontSize: 13,
                          display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: viewAsId ? 600 : 500,
                          background: viewAsId ? 'rgba(139,92,246,.16)' : 'transparent',
                          border: `1px solid ${viewAsId ? 'rgba(139,92,246,.5)' : 'var(--border)'}`,
                          color: viewAsId ? '#C4B5FD' : 'var(--text-3)',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <Eye size={13} />
                        {viewAsId ? 'Previewing' : 'View as member'}
                      </button>

                      {viewAsId && (
                        <select
                          className="usage-date-input"
                          value={viewAsId}
                          onChange={(e) => { setSelectedKey(null); setViewAsId(e.target.value); }}
                          aria-label="Member to preview"
                          style={{ maxWidth: 200 }}
                        >
                          {subAccounts.map((s) => (
                            <option key={s.client_id} value={s.client_id}>
                              {s.client_name || s.client_email}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
                <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  {period === 'custom' && (
                    <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                      <input
                        type="date"
                        className="usage-date-input"
                        value={customRange.from}
                        min={USAGE_FLOOR}
                        max={customRange.to || todayStr()}
                        onChange={e => setCustomRange(r => ({ ...r, from: e.target.value }))}
                        aria-label="From date"
                      />
                      <span style={{ color: 'var(--text-4)', fontSize: 12 }}>→</span>
                      <input
                        type="date"
                        className="usage-date-input"
                        value={customRange.to}
                        min={customRange.from || USAGE_FLOOR}
                        max={todayStr()}
                        onChange={e => setCustomRange(r => ({ ...r, to: e.target.value }))}
                        aria-label="To date"
                      />
                    </div>
                  )}
                  <div className="row no-scrollbar" style={{ gap: 4, padding: 4, borderRadius: 10, background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)' }}>
                    {PERIOD_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setPeriod(opt.value)}
                        aria-pressed={period === opt.value}
                        style={periodBtnStyle(period === opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                    <button
                      key="custom"
                      onClick={() => setPeriod('custom')}
                      aria-pressed={period === 'custom'}
                      style={periodBtnStyle(period === 'custom')}
                    >
                      Custom
                    </button>
                  </div>
                </div>
              </div>
              {period === 'custom' && !customReady ? (
                <div style={{ fontSize: 12, color: 'var(--text-4)', marginBottom: 18, textAlign: 'right' }}>
                  Pick a start and end date (earliest Jun 1, 2026).
                </div>
              ) : rangeCaption ? (
                <div className="usage-range-caption" style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 18, textAlign: 'right' }}>
                  {rangeCaption}
                </div>
              ) : null}

              {/* ─── Preview banner ─── every number below belongs to the member,
                   not the admin, so this must be impossible to miss ─── */}
              {viewingAs && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 10, background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.4)', marginBottom: 18, flexWrap: 'wrap' }}>
                  <Eye size={16} color="#C4B5FD" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#DDD6FE' }}>
                      Viewing as {viewingAs.client_name || viewingAs.client_email} — this is exactly what they see
                    </div>
                    {previewScopeText && (
                      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{previewScopeText}</div>
                    )}
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => { setSelectedKey(null); setViewAsId(null); }}
                    style={{ flexShrink: 0 }}
                  >
                    Exit preview
                  </button>
                </div>
              )}

              {/* ─── Shared-access notice ─── */}
              {sharedNotice && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px', borderRadius: 10, background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.22)', marginBottom: 18 }}>
                  <Users size={15} color="#8B5CF6" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.45 }}>{sharedNotice}</span>
                </div>
              )}

              {/* ─── Key filter chips ─── */}
              {!loading && perKey.length > 1 && (
                <div className="row usage-keychips no-scrollbar" style={{ gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                  <button
                    className="chip"
                    onClick={() => setSelectedKey(null)}
                    style={{ cursor: 'pointer', borderColor: selectedKey === null ? 'rgba(255,255,255,.35)' : undefined, color: selectedKey === null ? '#fff' : undefined }}
                  >
                    All keys
                  </button>
                  {perKey.map(k => {
                    const color = keyColorMap[k.client_api_key_id];
                    const active = selectedKey === k.client_api_key_id;
                    return (
                      <button
                        key={k.client_api_key_id}
                        className="chip"
                        onClick={() => toggleKey(k.client_api_key_id)}
                        title={descriptionById[k.client_api_key_id] || k.api_key_prefix || undefined}
                        style={{
                          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                          borderColor: active ? `${color}66` : undefined,
                          background: active ? `${color}18` : undefined,
                          color: active ? '#fff' : undefined,
                        }}
                      >
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
                        {k.api_key_name}
                      </button>
                    );
                  })}
                </div>
              )}

              {(period === 'custom' && !customReady) ? null : loading ? (
                <>
                  {/* Skeleton layout — no full-screen spinner, no layout jump */}
                  <div className="usage-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))', gap: 12, marginBottom: 20 }}>
                    {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} h={84} r={12} />)}
                  </div>
                  <Skeleton h={300} r={16} />
                  <div style={{ height: 20 }} />
                  <Skeleton h={200} r={16} />
                </>
              ) : error ? (
                <div className="card" style={{ padding: '40px 24px', textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Couldn't load usage data</div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 16 }}>Check your connection and try again.</div>
                  <button className="btn btn-ghost btn-sm" onClick={fetchUsage}>Retry</button>
                </div>
              ) : isEmpty ? (
                <div className="card" style={{ padding: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>No usage yet</div>
                  <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Calls made with your API keys will appear here.</div>
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate('/docs')} style={{ marginTop: 6 }}>View API docs</button>
                </div>
              ) : (
                <>
                  {/* ─── KPI grid ─── */}
                  <div className="usage-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))', gap: 12, marginBottom: 20 }}>
                    <KpiCard icon={DollarSign} label="Total spend" value={`$${totalCost.toFixed(2)}`} sub="All services" color="#10B981" />
                    <KpiCard icon={Activity} label="Total calls" value={fmt((summary.total_calls || 0) + (summary.total_image_jobs || 0) + (summary.total_workflow_runs || 0))} sub="LLM + jobs" color="#6366F1" />
                    <KpiCard icon={Cpu} label="LLM calls" value={fmt(summary.total_calls || 0)} sub={`in ${fmt(summary.total_input_tokens || 0)} · out ${fmt(summary.total_output_tokens || 0)} tokens`} color="#3B82F6" />
                    <KpiCard icon={Image} label="Image jobs" value={fmt(summary.total_image_jobs || 0)} color="#EC4899" />
                    <KpiCard icon={GitBranch} label="Workflows" value={fmt(summary.total_workflow_runs || 0)} color="#A78BFA" />
                    <KpiCard icon={Key} label="API keys" value={summary.active_keys ?? perKey.length} sub="total keys" color="#F59E0B" />
                  </div>

                  {/* ─── Insights strip ─── */}
                  {insights.length > 0 && (
                    <div className="usage-insights" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
                      {insights.map((ins, i) => {
                        const insColors = { cost: '#EF4444', calls: '#6366F1', up: '#F59E0B', down: '#10B981', peak: '#3B82F6', idle: '#8B5CF6' };
                        const c = insColors[ins.type] || '#6366F1';
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', borderRadius: 10, background: `${c}10`, border: `1px solid ${c}28`, flex: '1 1 260px', minWidth: 220 }}>
                            <div style={{ width: 6, height: 6, borderRadius: 2, background: c, flexShrink: 0, marginTop: 5 }} />
                            <span style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.4 }}>{ins.text}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* ─── Spend by key + Calls by key ─── */}
                  {seriesByKey.length > 0 && (
                    <div className="usage-chart-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                      <div className="card">
                        <SectionHeader title="Spend by key" sub="Total cost ($) stacked per API key" />
                        <div className="usage-chart-body" style={{ padding: 20, height: 300 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={spendChartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                              <XAxis dataKey="bucket" stroke={CHART_AXIS} fontSize={11} tickFormatter={bucketTick} />
                              <YAxis stroke={CHART_AXIS} fontSize={11} tickFormatter={v => `$${v < 1 ? v.toFixed(3) : v.toFixed(1)}`} />
                              <RechartsTooltip {...tt} labelFormatter={bucketTickFull} formatter={(v, name) => [`$${Number(v).toFixed(4)}`, name]} />
                              <Legend iconSize={10} wrapperStyle={{ fontSize: 11, cursor: 'pointer' }} onClick={handleLegendClick} formatter={(value, entry) => (<span style={{ opacity: selectedKey && selectedKey !== entry.dataKey ? 0.3 : 1, transition: 'opacity 0.15s' }}>{value}</span>)} />
                              {activeSeriesKeys.map(k => (
                                <Bar key={k.client_api_key_id} dataKey={k.client_api_key_id} name={k.api_key_name} stackId="s" fill={keyColorMap[k.client_api_key_id]} opacity={selectedKey && selectedKey !== k.client_api_key_id ? 0.12 : 1} onClick={() => toggleKey(k.client_api_key_id)} cursor="pointer" />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="card">
                        <SectionHeader title="Calls by key" sub="Total calls (LLM + image + workflow) stacked per key" />
                        <div className="usage-chart-body" style={{ padding: 20, height: 300 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={callsChartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                              <XAxis dataKey="bucket" stroke={CHART_AXIS} fontSize={11} tickFormatter={bucketTick} />
                              <YAxis stroke={CHART_AXIS} fontSize={11} tickFormatter={v => fmt(v)} />
                              <RechartsTooltip {...tt} labelFormatter={bucketTickFull} formatter={(v, name) => [Number(v).toLocaleString(), name]} />
                              <Legend iconSize={10} wrapperStyle={{ fontSize: 11, cursor: 'pointer' }} onClick={handleLegendClick} formatter={(value, entry) => (<span style={{ opacity: selectedKey && selectedKey !== entry.dataKey ? 0.3 : 1, transition: 'opacity 0.15s' }}>{value}</span>)} />
                              {activeSeriesKeys.map(k => (
                                <Bar key={k.client_api_key_id} dataKey={k.client_api_key_id} name={k.api_key_name} stackId="c" fill={keyColorMap[k.client_api_key_id]} opacity={selectedKey && selectedKey !== k.client_api_key_id ? 0.12 : 1} onClick={() => toggleKey(k.client_api_key_id)} cursor="pointer" />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ─── Key leaderboard ─── */}
                  {perKey.length > 0 && (
                    <div className="card" style={{ marginBottom: 20 }}>
                      <SectionHeader
                        title="Key leaderboard"
                        // Driven off sharedNotice, not has_shared_access: a
                        // 'selected' scope whose grants all resolve to nothing
                        // must not claim shared keys are included.
                        sub={`Ranked by total cost${isMainAccount ? ' · Includes all sub-accounts' : sharedNotice ? ' · Includes usage shared with you' : ''}`}
                      />
                      <div style={{ paddingTop: 8, paddingBottom: 20 }}>
                        <KeyLeaderboard perKey={perKey} keyColorMap={keyColorMap} totalCost={totalCost} selectedKey={selectedKey} onSelectKey={toggleKey} descriptionById={descriptionById} selfClientId={selfClientId} onToggleKeyActive={handleToggleKeyActive} togglingKeyId={togglingKeyId} canManageKeys={data?.access?.can_manage_api_keys !== false} />
                      </div>
                    </div>
                  )}

                  {/* ─── Peak activity + Token usage ─── */}
                  {(peakByKey.length > 0 || tokensSeries.length > 0) && (
                    <div className="usage-chart-grid" style={{ display: 'grid', gridTemplateColumns: peakByKey.length > 0 && tokensSeries.length > 0 ? '1fr 1fr' : '1fr', gap: 20, marginBottom: 20 }}>
                      {peakByKey.length > 0 && (
                        <div className="card">
                          <SectionHeader title={isCustomRange ? 'Peak activity — last day of range' : 'Peak activity — last 24h'} sub="Hourly call volume per key" />
                          <div className="usage-chart-body" style={{ padding: 20, height: 260 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={peakCallsData}>
                                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                                <XAxis dataKey="bucket" stroke={CHART_AXIS} fontSize={11} tickFormatter={fmtHour} />
                                <YAxis stroke={CHART_AXIS} fontSize={11} />
                                <RechartsTooltip {...tt} labelFormatter={fmtHourFull} formatter={(v, name) => [Number(v).toLocaleString(), name]} />
                                <Legend iconSize={10} wrapperStyle={{ fontSize: 11, cursor: 'pointer' }} onClick={handleLegendClick} formatter={(value, entry) => (<span style={{ opacity: selectedKey && selectedKey !== entry.dataKey ? 0.3 : 1, transition: 'opacity 0.15s' }}>{value}</span>)} />
                                {activePeakKeys.map(k => (
                                  <Bar key={k.client_api_key_id} dataKey={k.client_api_key_id} name={k.api_key_name} stackId="p" fill={keyColorMap[k.client_api_key_id]} opacity={selectedKey && selectedKey !== k.client_api_key_id ? 0.12 : 1} onClick={() => toggleKey(k.client_api_key_id)} cursor="pointer" />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}

                      {tokensSeries.length > 0 && (
                        <div className="card">
                          <SectionHeader title="Token usage" sub="LLM input vs output tokens over time" />
                          <div className="usage-chart-body" style={{ padding: 20, height: 260 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={tokensSeries}>
                                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                                <XAxis dataKey="bucket" stroke={CHART_AXIS} fontSize={11} tickFormatter={bucketTick} />
                                <YAxis stroke={CHART_AXIS} fontSize={11} tickFormatter={v => fmt(v)} />
                                <RechartsTooltip {...tt} labelFormatter={bucketTickFull} formatter={(v, name) => [Number(v).toLocaleString(), name]} />
                                <Legend />
                                <Bar dataKey="input_tokens" name="Input tokens" fill="#3B82F6" stackId="tok" />
                                <Bar dataKey="output_tokens" name="Output tokens" fill="#F97316" stackId="tok" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Disable / enable dialog. A real dialog rather than window.confirm
          because this is the ONLY surface where a grantee acts on a key they do
          not own — the reason field is the whole point of the audit log there,
          and a native confirm cannot collect one. */}
      {powerForm && (
        <div
          onClick={() => !togglingKeyId && setPowerForm(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000, display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 20,
            background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)',
          }}
        >
          <div
            className="card"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 440, maxWidth: '100%', padding: 20 }}
          >
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>
              {powerForm.next ? 'Enable API key' : 'Disable API key'}
            </h3>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 14 }}>
              {powerForm.key.api_key_name}
              {powerForm.key.owner_name ? ` · ${powerForm.key.owner_name}` : ''}
            </div>

            <div style={{
              padding: '10px 14px', borderRadius: 9, fontSize: 12, lineHeight: 1.5, marginBottom: 14,
              background: powerForm.next ? 'rgba(16,185,129,.08)' : 'rgba(245,158,11,.08)',
              border: `1px solid ${powerForm.next ? 'rgba(16,185,129,.2)' : 'rgba(245,158,11,.2)'}`,
              color: powerForm.next ? '#6EE7B7' : '#FBBF24',
            }}>
              {powerForm.next
                ? 'Requests using this key will be accepted again. It may have been disabled for a reason — check before enabling.'
                : 'Requests using this key will start failing immediately. It can be enabled again at any time.'}
            </div>

            {powerForm.key.is_shared && (
              <div style={{
                padding: '10px 14px', borderRadius: 9, fontSize: 12, lineHeight: 1.5, marginBottom: 14,
                background: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.2)', color: '#A5B4FC',
              }}>
                This key belongs to {powerForm.key.owner_name || 'another account'}. They will see who
                changed it and why.
              </div>
            )}

            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 6 }}>
              Reason (optional)
            </div>
            <input
              value={powerReason}
              onChange={(e) => setPowerReason(e.target.value)}
              maxLength={255}
              placeholder={powerForm.next ? 'e.g. Verified — safe to re-enable' : 'e.g. Rotating credentials'}
              style={{
                width: '100%', height: 38, padding: '0 12px', borderRadius: 9,
                background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)',
                color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
              }}
            />

            {powerError && (
              <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.5, color: '#FBBF24' }}>
                {powerError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button
                onClick={() => setPowerForm(null)}
                disabled={!!togglingKeyId}
                style={{
                  height: 34, padding: '0 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                  background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-3)',
                }}
              >
                {powerError ? 'Close' : 'Cancel'}
              </button>
              <button
                onClick={applyKeyPower}
                disabled={!!togglingKeyId}
                style={{
                  height: 34, padding: '0 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: powerForm.next ? 'rgba(16,185,129,.12)' : 'rgba(245,158,11,.12)',
                  border: `1px solid ${powerForm.next ? 'rgba(16,185,129,.4)' : 'rgba(245,158,11,.4)'}`,
                  color: powerForm.next ? '#10B981' : '#F59E0B',
                  opacity: togglingKeyId ? 0.6 : 1,
                }}
              >
                {togglingKeyId
                  ? (powerForm.next ? 'Enabling…' : 'Disabling…')
                  : (powerForm.next ? 'Enable key' : 'Disable key')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppScreenWrapper>
  );
}
